import { HugeiconsIcon, type HugeiconsIconProps } from '@hugeicons/react';
import type { IconSvgObject } from '@/lib/icon-types';
import { cn } from '@/lib/utils';

/** The icon sizes on the scale (§7.1). Walls use 24–32. */
const ICON_SIZES = [14, 16, 18, 20, 24, 28, 32] as const;
type IconSize = (typeof ICON_SIZES)[number];

export interface IconProps
  extends Omit<
    HugeiconsIconProps,
    'icon' | 'size' | 'strokeWidth' | 'altIcon' | 'showAlt' | 'children'
  > {
  icon: IconSvgObject;
  /** Pixels on the scale. Default 16. */
  size?: IconSize;
  /**
   * Makes the icon meaningful on its own ("Failed"). Leave it out when a word
   * next to the icon says the same thing: the icon is then hidden from
   * assistive technology.
   */
  label?: string;
  /** Default 1.5. */
  strokeWidth?: number;
}

/**
 * Every in-app icon (§7.1): Hugeicons at stroke 1.5 in `currentColor`, so the
 * icon takes the tone of the text around it. Decorative unless `label` is set.
 */
export function Icon({
  icon,
  size = 16,
  label,
  strokeWidth = 1.5,
  className,
  ...props
}: Readonly<IconProps>) {
  return (
    <HugeiconsIcon
      icon={icon}
      size={size}
      strokeWidth={strokeWidth}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      data-slot="icon"
      className={cn('shrink-0', className)}
      {...props}
    />
  );
}
